/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { RegistrationLayout } from "../../components/RegistrationLayout";
import { useUserContext } from "../../hooks/cookieHooks";
import { FinalModulePageProps } from "./interfaces";
import { finalModulePageStyle } from "./styles";
import { URL } from "../../API/urlEndpoints";
import { ButtonClickType, ButtonLabel } from "../../constants/commons";
import useSTR from "react-str";
import { getData } from "../../utils/fetchers";
import { ShowMessage } from "../../components/ShowMessage";
import * as _ from "lodash";
import {
  displayConfirmationWhenWindowCloses,
  getButtonLabel,
  showDisplayHeadingAndContentFromLoadingStatus,
} from "../../utils/helpers";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CustomButton } from "../../atoms/CustomButton";
import {
  CustomButtonColor,
  CustomButtonTypes,
} from "../../atoms/CustomButton/interface";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import { OustAccessTypes } from "../../commons/interfaces";

export const FinalModulePage: React.FunctionComponent<FinalModulePageProps> = (
  props
) => {
  const { onSaveAndExitClick, SuccessFullSaveObj } = props;
  const isMobileView = useIsMobile();
  // State to maintain the label of the button
  const [buttonLabel, setButtonLabel] = React.useState<string>();
  // Calling the SSO Hook to retrieve user information of the logged in user
  const user = useUserContext();

  // [GET] On page load, for fetching the details of Basic Information
  const [
    ,
    {
      result: getFinalModulePageData,
      revalidator: refetchFinalModulePageData,
      loading: getFinalModuleLoading,
      error: getFinalModuleError,
    },
  ] = useSTR(getData);

  React.useEffect(() => {
    setButtonLabel(getButtonLabel(getFinalModulePageData?.oust_status));
  }, [getFinalModulePageData]);

  // Function which is called whewn the browser closes
  displayConfirmationWhenWindowCloses();

  // On page load, calling the GET API
  React.useEffect(() => {
    refetchFinalModulePageData(
      `${URL.getOustDetails(_.get(user, "empId"), OustAccessTypes.ASSESSMENT)}`
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  const onPositiveActionClick = (buttonClickType: ButtonClickType) => {
    SuccessFullSaveObj(true);
  };

  // Callback from the parent Component to SAVE AND QUIT
  React.useEffect(() => {
    onSaveAndExitClick && onPositiveActionClick(ButtonClickType.SAVE_AND_EXIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSaveAndExitClick]);

  // Variable which shows the loading state
  const isScreenLoading = React.useMemo(() => {
    return getFinalModuleLoading;
  }, [getFinalModuleLoading]);

  //Function which is called when the START COURSES button is clicked
  const onStartCoursesClick = () => {
    setButtonLabel(ButtonLabel.CONTINUE);
    const newWindow = window.open(
      getFinalModulePageData?.oust_url?.url,
      "_blank",
      "noopener,noreferrer"
    );
    if (newWindow) newWindow.opener = null;
  };

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Hook which is called whenever there is an error in either GET/POST calls
  const showErrorComponent = React.useMemo(() => {
    return getFinalModuleError;
  }, [getFinalModuleError]);

  return (
    <span css={finalModulePageStyle}>
      <ShowMessage
        visible={isScreenLoading}
        loading={isScreenLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getFinalModuleLoading,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: getFinalModuleLoading,
          })?.loadingDescription
        }
      />
      <RegistrationLayout>
        <h2>Final Module</h2>
        <div>
          This module will help us understand your knowledge and skills.
        </div>
        <div className="d-flex">
          <div className={` ${isMobileView ? "mt-3  w-100" : "mt-5 ml-auto"}`}>
            <CustomButton
              className={`${isMobileView ? "mobile-width" : "desktop-width"}`}
              onClick={onStartCoursesClick}
              color={CustomButtonColor.PRIMARY}
              type={CustomButtonTypes.BUTTON}
            >
              <span className="responsive-text">{buttonLabel}</span>
            </CustomButton>
          </div>
        </div>
        {showErrorComponent && <ErrorComponent errorObj={showErrorComponent} />}
      </RegistrationLayout>
    </span>
  );
};
